import React, { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DashCardHeading from '../DashCardHeading';
import { Button, Loader } from 'ncoded-component-library';
import SettingsIcon from 'icons/Settings.icon';
import LightbulbOnIcon from 'icons/LightbulbOn.icon';
import {
  SUGGESTED_ACTION_MUTE_DAYS,
  SUGGESTED_ACTION_TYPE,
} from 'router/subrouters/DataRoom/models/Dashboard';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useSuggestedActionsQuery from '../../hooks/useSuggestedActionsQuery';
import { getArrayLength } from 'utils';
import SuggestedActionItem from './components/SuggestedActionItem';
import MutedSuggestionsDrawer from './components/MutedSuggestionsDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { removeActiveSuggestedAction } from './utils';
import useGetUndoActions from './hooks/useGetUndoActions';
import useBusEvent from 'hooks/useBusEvent';
import { StandardEventTypes } from 'modules/bus';
import SuggestedActionsEmptyState from './components/SuggestedActionsEmptyState';

import './SuggestedActions.styles.scss';

type SuggestedActionsProps = {
  className?: string;
};

const SuggestedActions: React.FC<SuggestedActionsProps> = (props) => {
  const { className } = props;

  const [mutedSuggestionsDrawer, mutedSuggestionsDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);

  const queryClient = useQueryClient();

  const {
    doesntHaveAccess,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { data, isLoading } = useSuggestedActionsQuery({
    dataRoomId,
    doesntHaveAccess,
  });

  const { t } = useTranslation();

  const activeSuggestions = data?.active;
  const pausedSuggestions = data?.paused;

  const numberOfActiveElements = getArrayLength(activeSuggestions);

  const classes = classNames(
    'suggested-actions',
    'white-panel',
    { 'has-actions': !!numberOfActiveElements },
    className,
  );

  const { undoNotifications, ...rest } = useGetUndoActions();

  const removeAddedFileSuggestion = useCallback(
    () =>
      removeActiveSuggestedAction(queryClient, [
        SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY,
        SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS,
      ]),
    [queryClient],
  );

  useBusEvent(
    StandardEventTypes.DATA_ROOM_NODE_ADDED,
    removeAddedFileSuggestion,
  );

  return (
    <div className={classes}>
      <DashCardHeading
        heading={t('suggestedActions')}
        description={t('suggestedActionsDescription', {
          count: SUGGESTED_ACTION_MUTE_DAYS,
        })}
        icon={LightbulbOnIcon}
        numberOfElements={numberOfActiveElements}
        showTimeframe={false}
      >
        <Button
          variant="icon"
          icon={SettingsIcon}
          onClick={() => mutedSuggestionsDrawer?.setOpen(true)}
        />
      </DashCardHeading>
      {isLoading && !doesntHaveAccess ? (
        <Loader overlay={false} />
      ) : !numberOfActiveElements ? (
        <SuggestedActionsEmptyState />
      ) : (
        <div className="suggested-actions__wrapper">
          {activeSuggestions.map((suggestion) => (
            <SuggestedActionItem
              key={suggestion.type}
              suggestion={suggestion}
              {...rest}
            />
          ))}
        </div>
      )}
      <MutedSuggestionsDrawer
        ref={mutedSuggestionsDrawerRef}
        pausedSuggestions={pausedSuggestions}
      />

      {undoNotifications}
    </div>
  );
};

export default SuggestedActions;
